import { PDFDocument, PDFPage, rgb, PDFFont } from "pdf-lib";
import type { SigningMetadata, SignatureEntry } from "./signing-metadata";

interface MetaPageData {
  documentHash: string;
  isLastSigner: boolean;
  existingMetadata: SigningMetadata | null;
  updatedSignatures: SignatureEntry[];
  titleOverride?: string;
  statusOverride?: string;
  statusColorOverride?: { r: number; g: number; b: number };
}

interface RenderContext {
  pdfDoc: PDFDocument;
  helvetica: PDFFont;
  customFont: PDFFont;
  customFontBold: PDFFont;
  renderFieldToImage: (filled: any, pxW: number, pxH: number) => Promise<any>;
}

const mmToPoints = (mm: number) => mm * 2.83465;

/**
 * Generate the Certificate of Signature audit page
 *
 * COORDINATE SYSTEM:
 * - Origin: Bottom-Left (0, 0)
 * - Page Size: 595 x 842 pts (A4)
 * - Y-axis flows DOWNWARD (decreasing values from top)
 *
 * LAYOUT REFERENCE:
 * - Top margin: 50pt (y: 792)
 * - Side margins: 50pt
 * - Content width: 495pt (545 - 50)
 */
export async function generateMetaPage(
  pdfDoc: PDFDocument,
  data: MetaPageData,
  ctx: RenderContext,
): Promise<void> {
  const { documentHash, isLastSigner, existingMetadata, updatedSignatures } =
    data;
  const { helvetica, customFont, customFontBold, renderFieldToImage } = ctx;

  // Rounded rect via drawSvgPath. Path is built in SVG coords (0,0 = top-left, Y down);
  // pdf-lib applies scale(1,-1) and translate(x,y), so we pass x,y = page position of
  // the rect's bottom-left and build path as 0..W, 0..H with origin at top-left.
  const drawRoundedRect = (page: PDFPage, options: any) => {
    const {
      x,
      y,
      width,
      height,
      borderRadius,
      color,
      borderColor,
      borderWidth,
      opacity,
    } = options;
    const X = Number(x);
    const Y = Number(y);
    const W = Number(width);
    const H = Number(height);
    const R = Math.min(Number(borderRadius), W / 2, H / 2);

    // Path in SVG space: top-left (0,0), size W x H, rounded corners
    const path = [
      `M ${R} 0`,
      `L ${W - R} 0`,
      `A ${R} ${R} 0 0 1 ${W} ${R}`,
      `L ${W} ${H - R}`,
      `A ${R} ${R} 0 0 1 ${W - R} ${H}`,
      `L ${R} ${H}`,
      `A ${R} ${R} 0 0 1 0 ${H - R}`,
      `L 0 ${R}`,
      `A ${R} ${R} 0 0 1 ${R} 0`,
      `Z`,
    ].join(" ");

    // drawSvgPath only accepts x, y, scale (no width/height). Origin (0,0) of path goes to (x,y).
    // We want rect bottom-left at (X, Y), so we pass y = Y + H (path top = Y+H, path bottom = Y after flip).
    page.drawSvgPath(path, {
      x: X,
      y: Y + H,
      color: color ?? rgb(1, 1, 1),
      borderColor: borderColor ?? rgb(0, 0, 0),
      borderWidth: borderWidth ?? 0,
      opacity: opacity !== undefined ? opacity : 1,
      borderOpacity: opacity !== undefined ? opacity : 1,
    });
  };

  // Load background template (keep metaPdfDoc for continuation pages)
  let tailPage: PDFPage;
  let metaPdfDoc: PDFDocument | null = null;
  try {
    const metaPdfBytes = await fetch("/_meta.pdf").then((res) =>
      res.arrayBuffer(),
    );
    metaPdfDoc = await PDFDocument.load(metaPdfBytes);
    const [metaPage] = await pdfDoc.copyPages(metaPdfDoc, [0]);
    tailPage = pdfDoc.addPage(metaPage);
  } catch (err) {
    console.warn("Failed to load _meta.pdf background, using blank page", err);
    tailPage = pdfDoc.addPage([595, 842]); // A4 size blank fallback
  }

  const metaPages: PDFPage[] = [tailPage];
  const { width: pWidth, height: pHeight } = tailPage.getSize();
  const topMargin = 50;
  const sealY = pHeight - topMargin - 120;

  // Single source of truth for header layout – change here to update both first and continuation pages
  const LAYOUT = {
    docInfoTitleSize: 14,
    docInfoTitleDy: 60,
    statusDy: 15,
    hashDy: 20,
    initiatedBySize: 12,
    initiatedByDy: 15,
    creatorBoxHeight: 45,
    creatorBoxBorderRadius: mmToPoints(2.5),
    creatorBoxLine1FromTop: 18,
    creatorBoxLine2FromTop: 35,
    creatorBoxBlockDy: 65,
    signeesLineSize: 14,
    signeesLineDy: 20,
    // Shared verification block (creator + signers): email + badge + Key ID, Timestamp, IP, Location
    verificationBoxHeight: 75,
    verificationBoxGap: 10,
    inputsHeaderHeight: 30, // line + "Inputs Provided:" before field rows
  } as const;

  interface VerificationData {
    email: string;
    keyId: string;
    timestamp: string;
    ip: string;
    location: string;
    delegationNote?: string;
  }

  /**
   * Draw the full verification block (one rounded box): email, VERIFIED badge, Key ID, Timestamp, IP, Location.
   * Used for Initiated By (creator) and for each signer. Returns Y for next content.
   */
  const drawVerificationBlock = (
    page: PDFPage,
    yStart: number,
    data: VerificationData,
  ): number => {
    const H = LAYOUT.verificationBoxHeight;
    const boxBottomY = yStart - H;
    drawRoundedRect(page, {
      x: 50,
      y: boxBottomY,
      width: pWidth - 100,
      height: H,
      borderRadius: LAYOUT.creatorBoxBorderRadius,
      color: rgb(0.98, 0.98, 0.98),
      borderColor: rgb(0.9, 0.9, 0.9),
      borderWidth: 1,
      opacity: 0.75,
    });
    const boxTopY = yStart;
    const emailY = boxTopY - 20;
    page.drawText(data.email, {
      x: 60,
      y: emailY,
      size: 11,
      font: customFontBold,
      color: rgb(0.05, 0.05, 0.05),
    });
    if (data.delegationNote) {
      page.drawText(data.delegationNote, {
        x: 60,
        y: emailY - 12,
        size: 8,
        font: customFont,
        color: rgb(0.4, 0.4, 0.4),
      });
    }
    page.drawRectangle({
      x: pWidth - 175,
      y: emailY - 3,
      width: 115,
      height: 15,
      color: rgb(0.9, 1, 0.9),
      opacity: 0.5,
    });
    const startMetaY = emailY - 18;
    const metaRows: [string, string][] = [
      ["Signing Key ID", data.keyId],
      ["Timestamp", data.timestamp],
      ["IP Address", data.ip],
      ["Location", data.location],
    ];
    metaRows.forEach((row, idx) => {
      const colX = idx % 2 === 0 ? 60 : 300;
      const rY = idx < 2 ? startMetaY : startMetaY - 14;
      page.drawText(`${row[0]}:`, {
        x: colX,
        y: rY,
        size: 8,
        font: helvetica,
        color: rgb(0.5, 0.5, 0.5),
      });
      page.drawText(row[1], {
        x: colX + 70,
        y: rY,
        size: 8,
        font: helvetica,
        color: rgb(0.2, 0.2, 0.2),
      });
    });
    return boxBottomY - LAYOUT.verificationBoxGap;
  };

  const createdAt = existingMetadata?.createdAt || new Date().toISOString();
  const creatorSig = existingMetadata?.creatorSignature;
  const creatorEmail = creatorSig?.serverSignature?.signerEmail || "Unknown";
  const creatorIP = creatorSig?.serverSignature?.ip || "N/A";
  const creatorLoc = creatorSig?.serverSignature?.location || "N/A";

  const drawText = (
    text: string,
    x: number,
    y: number,
    size: number,
    font = customFont,
    color = rgb(0, 0, 0),
  ) => {
    tailPage.drawText(text, { x, y, size, font, color });
  };

  /**
   * Draw the full meta header (Document Info + Initiated By + creator box + Signees line) on a page.
   * Returns the Y position for the next content (first signature block). All Y math uses LAYOUT.
   */
  const drawMetaHeader = (
    page: PDFPage,
    yStart: number,
    signeesLabel: string,
  ): number => {
    let y = yStart;

    if (data.titleOverride) {
      // TODO: Add title override - but remove the title from the static backdrop, maker text centered
      // page.drawText(data.titleOverride, { x: 50, y, size: 20, font: customFontBold, color: rgb(0, 0, 0) });
    }
    y -= LAYOUT.docInfoTitleDy;

    const statusText =
      data.statusOverride ||
      (isLastSigner
        ? "Signing Status: Complete"
        : "Signing Status: Incomplete (Pending Signatures)");
    const statusColor = data.statusColorOverride
      ? rgb(
          data.statusColorOverride.r,
          data.statusColorOverride.g,
          data.statusColorOverride.b,
        )
      : isLastSigner
        ? rgb(0, 0.6, 0)
        : rgb(0.8, 0.4, 0);

    page.drawText(statusText, {
      x: 50,
      y,
      size: 10,
      font: customFont,
      color: statusColor,
    });
    y -= LAYOUT.statusDy;

    page.drawText(`Document Hash: ${documentHash.slice(0, 32)}...`, {
      x: 50,
      y,
      size: 10,
      font: customFont,
      color: rgb(0.3, 0.3, 0.3),
    });
    y -= LAYOUT.hashDy;

    page.drawText("Document Signing Initiated By:", {
      x: 50,
      y,
      size: LAYOUT.initiatedBySize,
      font: customFontBold,
      color: rgb(0, 0, 0),
    });
    y -= LAYOUT.initiatedByDy;

    y = drawVerificationBlock(page, y, {
      email: creatorEmail,
      keyId: creatorSig?.keyId ?? "N/A",
      timestamp: createdAt,
      ip: creatorIP,
      location: creatorLoc,
    });

    y -= LAYOUT.initiatedByDy;

    page.drawText(signeesLabel, {
      x: 50,
      y,
      size: LAYOUT.signeesLineSize,
      font: customFontBold,
      color: rgb(0, 0, 0),
    });
    y -= LAYOUT.signeesLineDy;

    return y;
  };

  const sealSvg = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 200 200" width="400" height="400" class="coolshapes wheel-3 "><g clip-path="url(#cs_clip_1_wheel-3)"><mask id="cs_mask_1_wheel-3" style="mask-type:alpha" width="200" height="200" x="0" y="0" maskUnits="userSpaceOnUse"><path fill="#fff" d="M110 0H90l6.39 91.284-60.03-69.066L22.218 36.36l69.066 60.03L0 90v20l91.284-6.39-69.066 60.03 14.142 14.142 60.03-69.066L90 200h20l-6.39-91.284 60.03 69.066 14.142-14.142-69.066-60.03L200 110V90l-91.284 6.39 69.066-60.03-14.142-14.142-60.03 69.066L110 0z"></path></mask><g mask="url(#cs_mask_1_wheel-3"><path fill="#fff" d="M200 0H0v200h200V0z"></path><path fill="url(#paint0_linear_748_4839)" fill-opacity="0.55" d="M200 0H0v200h200V0z"></path><g filter="url(#filter0_f_748_4839)"><path fill="#18A0FB" d="M131 3H-12v108h143V3z"></path><path fill="#FF58E4" d="M190 109H0v116h190V109z"></path><ellipse cx="153.682" cy="64.587" fill="#FFD749" rx="83" ry="57" transform="rotate(-33.875 153.682 64.587)"></ellipse></g></g></g><defs><filter id="filter0_f_748_4839" width="361.583" height="346.593" x="-72" y="-61.593" color-interpolation-filters="sRGB" filterUnits="userSpaceOnUse"><feFlood flood-opacity="0" result="BackgroundImageFix"></feFlood><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"></feBlend><feGaussianBlur result="effect1_foregroundBlur_748_4839" stdDeviation="30"></feGaussianBlur></filter><linearGradient id="paint0_linear_748_4839" x1="200" x2="0" y1="0" y2="200" gradientUnits="userSpaceOnUse"><stop stop-color="#FF1F00"></stop><stop offset="1" stop-color="#FFD600"></stop></linearGradient><clipPath id="cs_clip_1_wheel-3"><path fill="#fff" d="M0 0H200V200H0z"></path></clipPath></defs></svg>`;

  const sealImg = await renderFieldToImage(
    {
      fieldId: "seal",
      type: "signature",
      value: sealSvg,
    } as any,
    200,
    200,
  );

  tailPage.drawImage(sealImg, { x: 520, y: sealY, width: 150, height: 150 });

  // Minimum Y (bottom margin) – we don't draw below this; triggers continuation page
  const MIN_Y = 80;
  const SIGNER_FIELD_ROW_HEIGHT = 20;
  const SIGNER_SIG_ROW_HEIGHT = 30;
  const verificationBlockTotalHeight =
    LAYOUT.verificationBoxHeight + LAYOUT.verificationBoxGap;

  const addContinuationPage = async () => {
    if (metaPdfDoc) {
      const [contPage] = await pdfDoc.copyPages(metaPdfDoc, [0]);
      tailPage = pdfDoc.addPage(contPage);
    } else {
      tailPage = pdfDoc.addPage([595, 842]);
    }
    metaPages.push(tailPage);
    tailPage.drawImage(sealImg, { x: 520, y: sealY, width: 150, height: 150 });
    return drawMetaHeader(
      tailPage,
      pHeight - topMargin,
      `Signees [${updatedSignatures.length}] Continued`,
    );
  };

  let yPos = drawMetaHeader(
    tailPage,
    pHeight - topMargin,
    `Signees [${updatedSignatures.length}]`,
  );

  for (const sig of updatedSignatures) {
    if (yPos < MIN_Y + verificationBlockTotalHeight) {
      yPos = await addContinuationPage();
    }

    const signerFields = (existingMetadata?.fields || []).filter(
      (f) => sig.sessionInput && sig.sessionInput[f.id],
    );
    const fieldRowHeight = (f: { type: string }) =>
      f.type === "signature" || f.type === "initials"
        ? SIGNER_SIG_ROW_HEIGHT
        : SIGNER_FIELD_ROW_HEIGHT;
    const serverMeta =
      sig.serverSignature ||
      ({} as Partial<import("./signing-metadata").ServerAttestation>);

    // Box 1: full verification block (email, VERIFIED badge, Key ID, Timestamp, IP, Location)
    // If delegated, show actual signer email with "(acting for <expected>)" context
    yPos = drawVerificationBlock(tailPage, yPos, {
      email: sig.email,
      keyId: sig.keyId,
      timestamp: sig.timestamp,
      ip: serverMeta.ip || "N/A",
      location: serverMeta.location || "N/A",
      delegationNote: sig.delegatedFrom
        ? `(acting for ${sig.delegatedFrom})`
        : undefined,
    });
    yPos += 5;

    // Box 2: Inputs Provided (separate rounded box; can span continuation pages)
    if (signerFields.length > 0) {
      const inputsHeaderHeight = LAYOUT.inputsHeaderHeight;
      const inputsContinuedHeaderHeight = 15;
      const headerRowHeight = 20; // Table header row height
      let fieldIndex = 0;

      while (fieldIndex < signerFields.length) {
        const isFirstChunk = fieldIndex === 0;
        const headerH = isFirstChunk ? 0 : inputsContinuedHeaderHeight; // No header for first chunk, just continuation header
        const availableHeight = yPos - MIN_Y - headerH;

        let chunkHeight = 0;
        const chunk: typeof signerFields = [];
        while (fieldIndex < signerFields.length) {
          const f = signerFields[fieldIndex];
          const rowH = fieldRowHeight(f);
          if (chunk.length > 0 && chunkHeight + rowH > availableHeight) break;
          chunk.push(f);
          chunkHeight += rowH;
          fieldIndex++;
        }

        // Box height includes: continuation header (if any) + table header row + data rows
        const boxHeight = headerH + headerRowHeight + chunkHeight;
        if (yPos - boxHeight < MIN_Y) {
          yPos = await addContinuationPage();
        }

        const boxBottomY = yPos - boxHeight;
        drawRoundedRect(tailPage, {
          x: 50,
          y: boxBottomY,
          width: pWidth - 100,
          height: boxHeight,
          borderRadius: LAYOUT.creatorBoxBorderRadius,
          color: rgb(1, 1, 1),
          borderColor: rgb(0.9, 0.9, 0.9),
          borderWidth: 1,
          opacity: 0.85,
        });

        const boxTopY = yPos;
        const tableLeft = 60;
        const tableRight = pWidth - 60;
        const tableWidth = tableRight - tableLeft;
        const colLabelWidth = 100;
        const colValueWidth = 200;
        const colLocationWidth = tableWidth - colLabelWidth - colValueWidth;
        const colLabelX = tableLeft;
        const colValueX = colLabelX + colLabelWidth;
        const colLocationX = colValueX + colValueWidth;

        if (isFirstChunk) {
          yPos = boxTopY;
        } else {
          drawText(
            `Inputs Provided (continued): ${sig.email}`,
            tableLeft,
            boxTopY - 15,
            9,
            helvetica,
            rgb(0.4, 0.4, 0.4),
          );
          yPos = boxTopY - inputsContinuedHeaderHeight;
        }

        // Table header row
        const headerY = yPos;

        tailPage.drawText("Field", {
          x: colLabelX + 5,
          y: headerY - 12,
          size: 8,
          font: helvetica,
          color: rgb(0.5, 0.5, 0.5),
        });
        tailPage.drawText("Value", {
          x: colValueX + 5,
          y: headerY - 12,
          size: 8,
          font: helvetica,
          color: rgb(0.5, 0.5, 0.5),
        });
        tailPage.drawText("Location", {
          x: colLocationX + 5,
          y: headerY - 12,
          size: 8,
          font: helvetica,
          color: rgb(0.5, 0.5, 0.5),
        });
        yPos = headerY - headerRowHeight;
        tailPage.drawLine({
          start: { x: tableLeft - 10, y: headerY - headerRowHeight },
          end: { x: tableRight + 10, y: headerY - headerRowHeight },
          thickness: 0.0125,
          color: rgb(0.6, 0.6, 0.6),
        });
        // Calculate total table height for vertical dividers
        let totalTableHeight = headerRowHeight;
        for (const field of chunk) {
          totalTableHeight += fieldRowHeight(field);
        }

        // Vertical column dividers (full height of table)
        tailPage.drawLine({
          start: { x: colValueX, y: headerY },
          end: { x: colValueX, y: headerY - totalTableHeight },
          thickness: 0.5,
          color: rgb(0.85, 0.85, 0.85),
        });
        tailPage.drawLine({
          start: { x: colLocationX, y: headerY },
          end: { x: colLocationX, y: headerY - totalTableHeight },
          thickness: 0.5,
          color: rgb(0.85, 0.85, 0.85),
        });

        // Table data rows
        for (let i = 0; i < chunk.length; i++) {
          const field = chunk[i];
          const rowH = fieldRowHeight(field);
          const val = sig.sessionInput[field.id];
          const label = field.labelText || field.type.toUpperCase();
          const rowY = yPos;

          // Label column
          tailPage.drawText(label, {
            x: colLabelX + 5,
            y: rowY - 12,
            size: 9,
            font: helvetica,
            color: rgb(0.4, 0.4, 0.4),
          });

          // Value column
          if (
            (field.type === "signature" || field.type === "initials") &&
            val.value.startsWith("<svg")
          ) {
            const sigImg = await renderFieldToImage(val, 100, 30);
            tailPage.drawImage(sigImg, {
              x: colValueX + 5,
              y: rowY - 30,
              width: 100,
              height: 30,
            });
          } else {
            let displayVal = val.value;
            if (field.type === "checkbox")
              displayVal =
                displayVal === "true" ? "[X] Checked" : "[ ] Unchecked";
            tailPage.drawText(displayVal, {
              x: colValueX + 5,
              y: rowY - 12,
              size: 9,
              font: helvetica,
              color: rgb(0, 0, 0),
            });
          }

          // Location column
          // drawText(`FieldId: ${field.id}, Page ${field.page}, Coord: ${field.x}, ${field.y}`, 300, yPos, 9, helvetica, rgb(0.8, 0.8, 0.8));
          const displayPage = field.page > 1 ? field.page - 1 : field.page;
          tailPage.drawText(
            `Page ${displayPage}, Position: (${field.x.toFixed(2)}, ${field.y.toFixed(2)})`,
            {
              x: colLocationX + 5,
              y: rowY - 12,
              size: 8,
              font: helvetica,
              color: rgb(0.6, 0.6, 0.6),
            },
          );
          // tailPage.drawText(`(${field.x}, ${field.y})`, { x: colLocationX + 5, y: rowY - 22, size: 7, font: helvetica, color: rgb(0.7, 0.7, 0.7) });

          // Row separator line
          if (i < chunk.length - 1) {
            tailPage.drawLine({
              start: { x: tableLeft - 10, y: rowY - rowH },
              end: { x: tableRight + 10, y: rowY - rowH },
              thickness: 0.3,
              color: rgb(0.9, 0.9, 0.9),
            });
          }

          yPos -= rowH;
        }
        yPos -= 5;
      }
    }

    yPos -= 20;
  }

  // Page n of N on each meta page when there is more than one
  if (metaPages.length > 1) {
    const total = metaPages.length;
    const footerSize = 9;
    const footerY = 28;
    for (let i = 0; i < total; i++) {
      const label = `Audit Page ${i + 1} of ${total}`;
      const textWidth = helvetica.widthOfTextAtSize(label, footerSize);
      metaPages[i].drawText(label, {
        x: (pWidth - textWidth) / 2,
        y: footerY,
        size: footerSize,
        font: helvetica,
        color: rgb(0.4, 0.4, 0.4),
      });
    }
  }
}
